import { CurrencyPipe, SlicePipe, DatePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';

import { BankAccount } from '../../core/models/bank-account.model';
import { BankService } from '../../core/services/bank.service';
import { PageHeaderComponent } from '../../shared/page-header/page-header';
import { SummaryCardComponent } from '../../shared/summary-card/summary-card';
import { LoadingComponent } from '../../shared/loading/loading';
import { SelectComponent, SelectOption } from '../../shared/select/select';
import { UiButtonComponent } from '../../shared/ui-button/ui-button';

@Component({
  selector: 'app-cuentas-bancarias',
  standalone: true,
  imports: [
    CurrencyPipe,
    SlicePipe,
    DatePipe,
    PageHeaderComponent,
    SummaryCardComponent,
    LoadingComponent,
    SelectComponent,
    UiButtonComponent,
    RouterLink,
  ],
  templateUrl: './cuentas-bancarias.html',
  styleUrl: './cuentas-bancarias.css',
})
export class CuentasBancariasComponent implements OnInit {
  protected banks: BankAccount[] = [];
  protected lastUpdate = '—';
  protected loadingPage = true;
  protected loadingMovements = false;
  protected movementsLoaded = false;
  protected movementError = '';

  protected limitOptions: SelectOption[] = [
    { value: '3', label: '3' },
    { value: '5', label: '5' },
    { value: '10', label: '10' },
  ];

  private readonly limits = new Map<string, number>();
  private readonly bankService = inject(BankService);

  ngOnInit(): void {
    this.bankService.accountsLoaded().subscribe((loaded) => {
      this.loadingPage = !loaded;
    });
    this.loadAccounts();
    this.loadLastUpdate();
  }

  protected get totalAvailable(): number {
    return this.banks
      .filter((bank) => bank.bankCode !== 'INTERNACIONAL')
      .reduce((total, bank) => total + bank.available, 0);
  }
  protected get totalAccounting(): number {
    return this.banks.reduce((total, bank) => total + bank.accounting, 0);
  }
  protected get inTransit(): number {
    return this.totalAccounting - this.totalAvailable;
  }
  protected displayAmount(amount: number): number {
    return Math.abs(amount);
  }
  protected integrationStatusLabel(status?: string): string {
    switch (status) {
      case 'active':
        return 'Integración activa';
      case 'missing':
        return 'Sin integración';
      case 'inactive':
        return 'Integración inactiva';
      case 'review':
        return 'Requiere revisión';
      default:
        return 'Estado no disponible';
    }
  }
  protected movementLimit(bankId: string): number {
    return this.limits.get(bankId) ?? 5;
  }
  protected setMovementLimit(bankId: string, limit: string | string[] | null): void {
    const value = Array.isArray(limit) ? limit[0] : limit;
    const numericLimit = Number(value);
    if (Number.isFinite(numericLimit) && numericLimit > 0) this.limits.set(bankId, numericLimit);
  }

  protected loadRecentMovements(): void {
    if (this.loadingMovements || !this.banks.length) return;

    this.loadingMovements = true;
    this.movementError = '';
    this.bankService.loadRecentMovements().subscribe({
      next: () => {
        this.loadingMovements = false;
        this.movementsLoaded = true;
      },
      error: () => {
        this.loadingMovements = false;
        this.movementError = 'No se pudieron cargar los movimientos recientes.';
      },
    });
  }

  private loadAccounts(): void {
    this.bankService.getAccounts().subscribe((accounts) => {
      this.banks = accounts;
    });
  }

  private loadLastUpdate(): void {
    this.bankService.getLastUpdate().subscribe((updatedAt) => {
      this.lastUpdate = updatedAt ?? '—';
    });
  }
}
